/**
 * <cart-shipping-rate>
 *
 * Fetches the real shipping rate for the current cart from Shopify's
 * Shipping and delivery settings and renders it into the cart drawer.
 *
 * Because the element lives inside the theme's data-merge="footer" block,
 * it is destroyed and recreated on every AJAX cart update, so connectedCallback
 * re-runs and the rate stays in sync with the cart contents.
 */
class CartShippingRate extends HTMLElement {
  connectedCallback() {
    this.valueEl = this.querySelector('[data-shipping-value]');
    if (!this.valueEl) return;

    this.cacheKey = `shipping-rate:${this.dataset.cartKey}:${this.dataset.country}`;

    const cached = this.readCache();
    if (cached) {
      this.render(cached);
      return;
    }

    this.fetchRate();
  }

  readCache() {
    try {
      const raw = sessionStorage.getItem(this.cacheKey);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  writeCache(value) {
    try {
      sessionStorage.setItem(this.cacheKey, JSON.stringify(value));
    } catch (e) {
      /* private mode / quota - not worth failing over */
    }
  }

  async fetchRate() {
    const params = new URLSearchParams();
    params.set('shipping_address[country]', this.dataset.country);
    if (this.dataset.province) params.set('shipping_address[province]', this.dataset.province);
    if (this.dataset.zip) params.set('shipping_address[zip]', this.dataset.zip);

    const url = `${this.dataset.rootUrl.replace(/\/$/, '')}/cart/shipping_rates.json?${params}`;

    try {
      const response = await fetch(url, { headers: { Accept: 'application/json' } });

      // 422 means the address wasn't enough to resolve a zone, or nothing ships there.
      if (!response.ok) return;

      const data = await response.json();
      const rates = data.shipping_rates || [];
      if (!rates.length) return;

      const cheapest = rates.reduce((lowest, rate) =>
        parseFloat(rate.price) < parseFloat(lowest.price) ? rate : lowest
      );

      const result = {
        price: parseFloat(cheapest.price),
        name: cheapest.presentment_name || cheapest.name
      };

      this.writeCache(result);
      this.render(result);
    } catch (e) {
      /* leave the row hidden rather than showing a wrong price */
    }
  }

  render({ price }) {
    const standardRate = parseInt(this.dataset.standardRate, 10) / 100;

    if (price === 0) {
      let html = `<span class="cart-shipping__free">${this.dataset.freeText}</span>`;
      if (standardRate > 0) {
        html += ` <s class="cart-shipping__was">${this.formatMoney(standardRate)}</s>`;
      }
      this.valueEl.innerHTML = html;
      this.classList.add('cart-shipping--free');
    } else {
      this.valueEl.textContent = this.formatMoney(price);
    }

    this.hidden = false;
  }

  formatMoney(amount) {
    try {
      return new Intl.NumberFormat(this.dataset.locale, {
        style: 'currency',
        currency: this.dataset.currency
      }).format(amount);
    } catch (e) {
      return amount.toFixed(2);
    }
  }
}

if (!customElements.get('cart-shipping-rate')) {
  customElements.define('cart-shipping-rate', CartShippingRate);
}
